import { useState, useId } from 'react';

export type FormatPreset = {
  id: string;
  name: string;
  format: string;
  sort: string;
  description: string;
};

export const FORMAT_PRESETS: FormatPreset[] = [
  {
    id: 'best_uncapped',
    name: '🌟 Best Quality (Uncapped)',
    format: 'bestvideo+bestaudio/best',
    sort: 'res,fps',
    description: 'Highest available quality without resolution limits',
  },
  {
    id: 'fhd_1080p',
    name: '📺 1080p Full HD (Balanced)',
    format: 'bestvideo[height<=1080]+bestaudio/best[height<=1080]',
    sort: 'res,fps',
    description: 'Max 1080p resolution, ideal for most displays',
  },
  {
    id: 'hd_720p',
    name: '💾 720p HD (Low Storage)',
    format: 'bestvideo[height<=720]+bestaudio/best[height<=720]',
    sort: 'res,fps',
    description: 'Max 720p resolution to save disk space',
  },
  {
    id: 'prefer_av1',
    name: '⚡ Prefer AV1 Codec (Apple M1-M4 / Modern Hardware)',
    format: 'bestvideo+bestaudio/best',
    sort: 'res,codec:av1',
    description: 'Prefers AV1 hardware decoding on modern GPUs & Apple Silicon',
  },
  {
    id: 'prefer_h265',
    name: '🎬 Prefer H.265 / HEVC (High Efficiency)',
    format: 'bestvideo[vcodec*=hevc]+bestaudio/bestvideo[vcodec*=h265]+bestaudio/best',
    sort: 'res,codec:hevc:h265',
    description: 'High efficiency H.265/HEVC video compression',
  },
  {
    id: 'prefer_h264',
    name: '📱 Prefer H.264 / AVC (Universal Compatibility)',
    format: 'bestvideo[height<=1080][vcodec*=avc1]+bestaudio[acodec*=mp4a]/mp4',
    sort: 'res,codec:avc1',
    description: 'Universally compatible with older TVs, Safari, and all browsers',
  },
  {
    id: 'apple_ios',
    name: '🍏 Apple Universal (iOS / Mac / QuickTime)',
    format: 'bv*[vcodec~=av01]+ba[acodec~=mp4a]/bv*[vcodec~=avc1]+ba[acodec~=mp4a]/best',
    sort: 'res,codec:av01:avc1',
    description: 'Native playback support for iPhone, iPad, Mac & QuickTime',
  },
];

type Props = {
  currentFormat: string | null;
  currentSort: string | null;
  onSelectPreset?: (format: string, sort: string) => void;
  onApplyPreset: (format: string, sort: string) => Promise<void> | void;
};

const FormatPresetSelector = ({
  currentFormat,
  currentSort,
  onSelectPreset,
  onApplyPreset,
}: Props) => {
  const selectId = useId();
  const [saving, setSaving] = useState(false);
  const [savedSuccess, setSavedSuccess] = useState(false);

  // Find matching preset based on current saved format & sort
  const matchedPreset = FORMAT_PRESETS.find(
    p =>
      (p.format === currentFormat || (!currentFormat && p.id === 'best_uncapped')) &&
      (p.sort === currentSort || (!currentSort && p.sort === 'res,fps')),
  );

  const activePresetId = matchedPreset ? matchedPreset.id : 'best_uncapped';
  const activePreset = FORMAT_PRESETS.find(p => p.id === activePresetId);

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    console.log("handleSelectChange triggered with value:", e.target.value);
    const val = e.target.value;
    const targetPreset = FORMAT_PRESETS.find(p => p.id === val);
    if (targetPreset && onSelectPreset) {
      console.log("Setting new format and sort in parent:", targetPreset.format, targetPreset.sort);
      onSelectPreset(targetPreset.format, targetPreset.sort);
    }
  };

  const handleApplyClick = async () => {
    if (!activePreset) return;
    setSaving(true);
    setSavedSuccess(false);

    try {
      await onApplyPreset(activePreset.format, activePreset.sort);
      setSavedSuccess(true);
      setTimeout(() => setSavedSuccess(false), 3000);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="format-preset-card">
      <div className="preset-header">
        <label htmlFor={selectId} className="preset-title-label">
          ⚡ Quick Format Profile Preset
        </label>
        <span className="preset-active-badge">
          {matchedPreset ? `Active: ${matchedPreset.name}` : 'Custom yt-dlp Entry'}
        </span>
      </div>

      <div className="preset-selector-row">
        <select
          id={selectId}
          value={activePresetId}
          onChange={handleSelectChange}
          className="preset-dropdown-select"
        >
          {!matchedPreset && (
            <option value="custom" disabled>
              ✏️ Custom Format Configuration
            </option>
          )}
          {FORMAT_PRESETS.map(preset => (
            <option key={preset.id} value={preset.id}>
              {preset.name}
            </option>
          ))}
        </select>

        <button
          type="button"
          onClick={handleApplyClick}
          disabled={saving}
          className="preset-apply-btn"
        >
          {saving ? 'Saving...' : 'Save & Apply Preset'}
        </button>
      </div>

      {savedSuccess && (
        <div className="preset-success-msg">
          ✅ Preset saved and applied successfully!
        </div>
      )}

      {activePreset && (
        <div className="preset-info-box">
          <p className="preset-description">💡 {activePreset.description}</p>
          <div className="preset-code-preview">
            <span>
              <strong>-f Format:</strong> <code>{activePreset.format}</code>
            </span>
            <span>
              <strong>--format-sort:</strong> <code>{activePreset.sort}</code>
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default FormatPresetSelector;
